import { useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Users } from 'lucide-react'

import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import { initializeMyPartners } from 'slices/myPartnersSlice'
import usePartnerProfileDialog from 'features/Partners/hooks/usePartnerProfileDialog'

/**
 * The Partners panel from the Home rightbar, as a component any screen can
 * drop in.
 *
 * It loads its own slice rather than relying on the host screen having already
 * done so, which is what lets it sit on screens (the Library pages) that have
 * no other reason to know about partners.
 *
 * A row opens the partner profile popup (MYD-533), the same one every other
 * partner list opens.
 */
function SidebarPartners() {
	const dispatch = useDispatch()
	const { openPartner, partnerDialog } = usePartnerProfileDialog()

	const {
		entities,
		ids,
		isLoading,
		isInitialized,
	} = useSelector((state: any) => state.myPartners)

	const partners = useMemo(
		() => (ids ?? []).map((id: string) => entities?.[id]).filter(Boolean),
		[ids, entities],
	)

	useEffect(() => {
		if (!isInitialized) dispatch(initializeMyPartners() as any)
	}, [dispatch, isInitialized])

	return (
		<div className="flex flex-col h-full">
			<div className="px-4 py-3 border-b border-gray-100">
				<Link
					to="/partners"
					className="flex items-center gap-2 text-gray-600 text-base font-semibold no-underline hover:scale-105 transition-transform duration-150"
				>
					<Users className="w-4 h-4" /> Partners
				</Link>
			</div>

			<div className="flex-1 overflow-y-auto">
				{isLoading && partners.length === 0 ? (
					<div className="flex items-center justify-center h-full p-4">
						<span className="text-sm text-gray-400">Loading...</span>
					</div>
				) : partners.length > 0 ? (
					<div>
						{partners.map((entry: any, index: number) => {
							const partner = entry?.partner ?? entry
							return (
								<button
									key={partner?._id ?? index}
									type="button"
									onClick={() => openPartner(partner?._id, partner)}
									className="w-full flex items-center px-4 py-2 text-left hover:bg-gray-50"
								>
									<Avatar className="h-9 w-9 mr-3 shrink-0">
										<AvatarImage src={partner?.image} />
										<AvatarFallback />
									</Avatar>
									<div className="flex-1 min-w-0">
										<div className="text-sm text-gray-900 truncate" title={partner?.name}>
											{partner?.name}
										</div>
										<div className="text-xs text-gray-500 truncate">
											{entry?.isAccepted ? partner?.company : 'Pending'}
										</div>
									</div>
								</button>
							)
						})}
					</div>
				) : (
					<div className="flex items-center justify-center h-full p-4">
						<Link to="/partners" className="no-underline">
							<h3 className="text-[#0080fe] opacity-30 hover:font-semibold hover:opacity-100 hover:scale-110 duration-150 text-sm font-light text-center">
								Add Partners to build Deal Teams for full collaboration.
							</h3>
						</Link>
					</div>
				)}
			</div>
			{partnerDialog}
		</div>
	)
}

export default SidebarPartners
